import {
  EMPTY_TASK_STATUS_COUNTS,
  IReportActor,
  IReportOverviewTasks,
  TaskStatusCounts,
  TaskTimeSeriesData
} from '@dadohr/shared/domainTypes/reports';
import { useTheme } from '@emotion/react';
import styled from '@emotion/styled';
import { Typography } from '@material-ui/core';
import { capitalize, sortBy } from 'lodash';
import moment from 'moment-timezone';
import React, { useMemo, useState } from 'react';
import {
  Bar,
  BarChart,
  CartesianGrid,
  Legend,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis
} from 'recharts';
import { WithShape } from '../../../../components/Charts/Util';
import { useHoverDurationTracking } from '../../../../hooks/useHoverDurationTracking';
import { useUnsafeTranslation } from '../../../../i18next';
import { Centered } from '../../../../layout/Centered';
import { FlexContainer } from '../../../../layout/Flex';
import { ActorSelector } from '../ActorSelector';
import { ChartTitle } from '../ChartTitle';
import { GraphUnitSelector } from '../GraphUnitSelector';

const HEIGHT = 375;

const wrapperStyle = {
  display: 'flex',
  alignItems: 'end',
  justifyContent: 'center'
};

const TooltipContainer = styled('div')`
  background-color: white;
  border-radius: 5px;
  padding: 5px;
  border: lightgray solid 1px;
`;

const TooltipItem = styled('div')`
  display: flex;
  justify-content: space-between;
  align-items: center;
  height: 20px;
  width: 200px;
`;

const LegendText = styled('div')((p) => ({
  color: p.theme.custom.colors.gray.w500
}));

const getRelevantTotal = (x: TaskStatusCounts) =>
  x.completedLate + x.completedOnTime + x.overdue + x.ongoing;

const CustomTooltip = ({
  payload,
  filter
}: {
  payload?: any;
  filter?: 'byWeek' | 'byMonth' | 'byCohort';
}) => {
  const { t } = useUnsafeTranslation();
  if (!payload) {
    return null;
  }
  const date = payload[0]?.payload.value;
  return (
    <TooltipContainer>
      {(payload || []).length > 0 && (
        <>
          <TooltipItem>
            <Typography variant="body2" style={{ marginBottom: '5px' }}>
              {filter === 'byMonth'
                ? moment(date).format(
                    `[ 01 - ${moment(date).daysInMonth()}] MMMM YYYY`
                  )
                : filter === 'byWeek'
                ? `${moment(date).startOf('isoWeek').format('DD')} - ${moment(
                    date
                  )
                    .endOf('isoWeek')
                    .format('DD MMMM YYYY')}`
                : moment(date).format(`D MMMM YYYY`)}
            </Typography>
          </TooltipItem>
          <TooltipItem>
            <WithShape color="lightGray" shape="square">
              {t('Total')}
            </WithShape>
            <p>{getRelevantTotal(payload[0].payload.counts)}</p>
          </TooltipItem>
        </>
      )}
      {payload.map((data: any) => {
        return (
          <React.Fragment key={data.dataKey}>
            {data.value > 0 && (
              <TooltipItem>
                <WithShape color={data.fill} shape="square">
                  {data.name}
                </WithShape>
                <p>{data.value}</p>
              </TooltipItem>
            )}
          </React.Fragment>
        );
      })}
    </TooltipContainer>
  );
};

export const TasksBlockBarChart = ({
  height = HEIGHT,
  data,
  actors,
  showActorFilter = false
}: {
  height?: number;
  data: IReportOverviewTasks;
  actors: IReportActor[];
  showActorFilter?: boolean;
}) => {
  const { t } = useUnsafeTranslation();
  const hoverTrackingProps = useHoverDurationTracking({
    name: 'Tasks By Status Chart Hovered',
    explanation:
      'Admin user hovered over the stacked bar chart showing tasks per week/month/cohort, by status'
  });
  const theme = useTheme();
  const [selectedFilter, setSelectedFilter] = useState<
    'byMonth' | 'byWeek' | 'byCohort'
  >('byCohort');
  const [selectedActors, setSelectedActors] = useState(
    actors.map((a) => a.key)
  );

  const chartData = useMemo(() => {
    const res: {
      [timeValue: string]: TaskTimeSeriesData;
    } = {};
    selectedActors.forEach((actorKey) => {
      const timeSeriesForActor = data.byActor[actorKey]?.[selectedFilter] || [];
      timeSeriesForActor.forEach((d) => {
        const container = (res[d.value] = res[d.value] || {
          value: d.value,
          counts: EMPTY_TASK_STATUS_COUNTS()
        });
        container.counts.total += d.counts.total;
        container.counts.completedLate += d.counts.completedLate;
        container.counts.completedOnTime += d.counts.completedOnTime;
        container.counts.upcoming += d.counts.upcoming;
        container.counts.skipped += d.counts.skipped;
        container.counts.ongoing += d.counts.ongoing;
        container.counts.overdue += d.counts.overdue;
      });
    });
    return sortBy(Object.values(res), (x) => x.value).filter(
      (x) => getRelevantTotal(x.counts) > 0
    );
  }, [data, selectedFilter, selectedActors]);

  return (
    <>
      <FlexContainer justifyContent="space-between" marginBottom={4}>
        <ChartTitle
          title={t('Total tasks')}
          tooltipText={t(
            'The number of tasks that started in a month/week or all tasks sent to a specific cohort'
          )}
        />
        <GraphUnitSelector
          selectedFilter={selectedFilter}
          selectedValue={(value) => {
            setSelectedFilter(value);
          }}
          title="tasks"
        />
      </FlexContainer>
      {showActorFilter && (
        <ActorSelector
          actors={actors}
          selected={selectedActors}
          setSelected={setSelectedActors}
          marginBottom={4}
          trackingKey="Tasks by status"
        />
      )}
      <div {...hoverTrackingProps}>
        <ResponsiveContainer width="100%" height={height}>
          {chartData.length > 0 ? (
            <BarChart
              data={chartData}
              barSize={50}
              margin={{ top: 20, right: 0, left: 0, bottom: 5 }}
            >
              <CartesianGrid vertical={false} />
              <XAxis
                dataKey="value"
                interval={
                  chartData.length > 32
                    ? 'preserveStartEnd'
                    : chartData.length > 16
                    ? 1
                    : 0
                }
                tickMargin={20}
                tickCount={chartData.length}
                tickFormatter={(tk: any) => {
                  switch (selectedFilter) {
                    case 'byCohort':
                      return moment(tk).format('DD MMM');
                    case 'byWeek':
                      return moment(tk).format(`DD MMM`);
                    case 'byMonth':
                      return moment(tk).format('MMMM');
                  }
                }}
              />
              <YAxis axisLine={false} tickSize={0} tickMargin={20} />
              <Tooltip
                cursor={false}
                content={<CustomTooltip filter={selectedFilter} />}
              />
              <Legend
                height={75}
                wrapperStyle={wrapperStyle}
                content={(props) => {
                  const { payload } = props;
                  if (!payload) {
                    return null;
                  }
                  return (
                    <FlexContainer spacing={6}>
                      {payload.map((e) => (
                        <WithShape
                          key={
                            e.id /*this type is wrong - id doesn't seem to exist - it's dataKey */ ||
                            (e as any).dataKey
                          }
                          color={e.color || 'black'}
                          shape="square"
                        >
                          <LegendText>{e.value}</LegendText>
                        </WithShape>
                      ))}
                    </FlexContainer>
                  );
                }}
              />
              <Bar
                dataKey="counts.completedOnTime"
                name={`${capitalize(t('done on time'))}`}
                stackId="a"
                fill={theme.custom.colors.green.w400}
                animationDuration={750}
              />
              <Bar
                dataKey="counts.completedLate"
                name={`${capitalize(t('done late'))}`}
                stackId="a"
                fill={theme.custom.colors.green.w500}
                animationDuration={750}
              />
              <Bar
                dataKey="counts.overdue"
                name={`${t('Overdue')}`}
                stackId="a"
                fill={theme.custom.colors.red.w400}
                animationDuration={750}
              />
              <Bar
                dataKey="counts.ongoing"
                name={`${t('To do')}`}
                stackId="a"
                fill={theme.custom.colors.blue.w400}
                animationDuration={750}
              />
            </BarChart>
          ) : (
            <Centered height={height}>
              <Typography>{t('No tasks match these criteria.')}</Typography>
            </Centered>
          )}
        </ResponsiveContainer>
      </div>
    </>
  );
};
